import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { initials } from '../../lib/mobileNav.ts';
import { MobileIcon } from './icons.tsx';

type SubtitleStore = {
  subtitle: string | null;
  setSubtitle: (next: string | null) => void;
};

const SubtitleContext = createContext<SubtitleStore>({
  subtitle: null,
  setSubtitle: () => {},
});

/**
 * Lets a screen add a second line to the shell's app bar.
 *
 * The title itself is derived from the pathname (`appBarTitle`), which is all
 * a list route needs. A subtitle, though, is data only the screen has — the
 * audit runner's `{plate} · {asset}` can't be read off the URL. Rather than
 * letting each such screen render its own bar (two bars, two heights, two sets
 * of safe-area bugs), the screen publishes a string here and the one bar in the
 * shell picks it up.
 *
 * Wrap the shell's subtree; AdminShell does.
 */
export function MobileAppBarProvider({ children }: { children: ReactNode }) {
  const [subtitle, setSubtitle] = useState<string | null>(null);
  const value = useMemo<SubtitleStore>(() => ({ subtitle, setSubtitle }), [subtitle]);
  return <SubtitleContext.Provider value={value}>{children}</SubtitleContext.Provider>;
}

/**
 * Publish an app-bar subtitle for as long as the calling screen is mounted; it
 * clears itself on unmount so a stale subtitle can never survive a navigation.
 * Pass null to show none.
 */
export function useMobileAppBarSubtitle(subtitle: string | null): void {
  const { setSubtitle } = useContext(SubtitleContext);
  useEffect(() => {
    setSubtitle(subtitle);
    return () => setSubtitle(null);
  }, [subtitle, setSubtitle]);
}

/**
 * Tailwind only emits a class it can find as a literal string in the source, so
 * the breakpoint cannot be interpolated into a template — `${bp}:hidden` compiles
 * to no CSS at all and the bar would show at every width. Both literals live
 * here instead.
 */
const HIDE_ABOVE: Record<'md' | 'lg', string> = { md: 'md:hidden', lg: 'lg:hidden' };

type MobileAppBarProps = {
  title: string;
  /**
   * The width at which the desktop header takes over. The admin app hands its
   * 260px sidebar back at `lg`; the driver portal, which has no sidebar, switches
   * at `md`.
   */
  hideAt: 'md' | 'lg';
  /** Back chevron — true on record-detail routes, which are entered from a list. */
  showBack?: boolean;
  /** Signed-in user's display name; only the initials are shown. */
  userName?: string;
  /**
   * Replaces the initials avatar in the right slot.
   *
   * The avatar is the default because the admin app hides everything actionable
   * behind its "More" tab. The driver portal has four tabs and no More, so Sign
   * out has nowhere else to live and passes a button here instead.
   *
   * Note the default avatar depends on `bg-brand-light`, whose
   * `--brand-primary-light` var is defined in apps/admin/src/index.css and
   * apps/portal/src/index.css. Any other app that wants the avatar rather than an
   * action has to define it first, or the circle renders transparent.
   */
  action?: ReactNode;
  /**
   * Replaces the title/subtitle block with a caller-owned lockup — the driver
   * portal's product mark plus its "name · company" identity line.
   *
   * A generic slot rather than a `BrandMark` prop, because this component is
   * shared: the admin app keeps the page title here, and a bar that reached for
   * BrandMark itself would put the product mark in both.
   *
   * `title` stays required and becomes the header's accessible name whenever a
   * brand is supplied, so a screen reader keeps the page context that sighted
   * users now read from the screen's own <h1>.
   */
  brand?: ReactNode;
};

/**
 * The 52px top bar that stands in for a desktop header below `hideAt`.
 * It carries the page title the per-screen <h1> used to (screens hide theirs on
 * mobile), so the shell — not the screen — owns the title and every route gets
 * a consistent one from `appBarTitle`.
 *
 * Unless the caller passes `brand`, which takes the slot instead and hands the
 * title back to the screen. The driver portal does that; the admin app does not.
 *
 * Hides itself above `hideAt` so the shells can render it unconditionally
 * alongside the real header (see BottomTabBar).
 */
export function MobileAppBar({
  title,
  hideAt,
  showBack,
  userName,
  action,
  brand,
}: MobileAppBarProps) {
  const navigate = useNavigate();
  const { subtitle } = useContext(SubtitleContext);

  return (
    <header
      className={`flex h-[52px] shrink-0 items-center gap-2.5 border-b border-slate-200 bg-white pl-4 pr-2 ${HIDE_ABOVE[hideAt]}`}
      // Only when a brand replaces the visible title: the banner would otherwise
      // announce the product name on every route and the page would lose its
      // only spoken title.
      {...(brand ? { 'aria-label': title } : {})}
    >
      {showBack && (
        <button
          type="button"
          // History-based rather than a computed parent route: every detail
          // screen here is reached from exactly one list, and going back keeps
          // that list's scroll position and filters.
          onClick={() => navigate(-1)}
          aria-label="Go back"
          className="-ml-3 flex h-11 w-11 shrink-0 items-center justify-center text-slate-500"
        >
          <MobileIcon name="chevron-left" size={22} />
        </button>
      )}
      {brand ? (
        <div className="flex min-w-0 flex-1 items-center gap-2.5">{brand}</div>
      ) : (
        <div className="min-w-0 flex-1">
          <div className="truncate text-[17px] font-bold tracking-[-0.01em] text-slate-900">
            {title}
          </div>
          {subtitle !== null && subtitle !== '' && (
            <div className="truncate text-[12px] text-slate-500">{subtitle}</div>
          )}
        </div>
      )}
      {action ?? (
        <div
          className="font-mono-eyebrow flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-brand-light text-[12px] font-extrabold tracking-[0.02em] text-brand-active"
          title={userName ?? ''}
        >
          {initials(userName)}
        </div>
      )}
    </header>
  );
}
